import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import MainContent from '../components/MainContent';
import Modal from '../components/Modal';
import downloadReport from '../utils/downloadReport';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import InvoiceForm from '../components/finance/InvoiceForm';
import HolderInvoiceDetail from '../components/finance/HolderInvoiceDetail';
import ConsultantPicker from '../components/finance/ConsultantPicker';
import FinanceReminders from '../components/finance/FinanceReminders';
import { StatusBadge } from '../components/finance/InvoiceFiles';
import {
  CARD,
  INPUT,
  PRIMARY,
  SECONDARY,
  STATUS_LABELS,
  auditDetails,
  currentPeriod,
  formatAmount,
  formatDateTime,
  periodLabel,
} from '../components/finance/financeText';
import {
  EXPORT_PATH,
  PAGE_SIZE,
  financeError,
  useAudit,
  useFinanceAccess,
  useGrantAccess,
  useGrants,
  useInvoices,
  useMissing,
  useRevokeAccess,
  useSendInvoiceReminders,
} from '../hooks/useFinance';

const TABS = [
  { key: 'invoices', label: 'Submissions' },
  { key: 'missing', label: 'Missing' },
  { key: 'reminders', label: 'Reminders' },
  { key: 'audit', label: 'Audit trail' },
  { key: 'access', label: 'Access' },
];

function Pager({ offset, count, total, onPage }) {
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex items-center justify-between border-t border-gray-200 px-5 py-2 text-xs text-gray-500">
      <span>
        {offset + 1}–{Math.min(offset + count, total)} of {total}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => onPage(offset - PAGE_SIZE)}
          className={SECONDARY}
        >
          Previous
        </button>
        <button
          type="button"
          disabled={offset + PAGE_SIZE >= total}
          onClick={() => onPage(offset + PAGE_SIZE)}
          className={SECONDARY}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function Submissions({ onOpen, onCreate }) {
  const [filters, setFilters] = useState({ period: currentPeriod() });
  const [consultant, setConsultant] = useState(null);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => ({ ...f, q: search.trim() }));
      setOffset(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);
  const { data, isLoading, error } = useInvoices(
    { ...filters, administratorId: consultant?.administrator_id },
    offset
  );
  const rows = data?.invoices ?? [];
  const total = data?.total ?? 0;
  const set = (changes) => {
    setFilters((f) => ({ ...f, ...changes }));
    setOffset(0);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadReport(
        EXPORT_PATH,
        `consultant-invoices-${filters.period || 'all'}.csv`,
        {
          client: axiosInstancev2,
          params: filters.period ? { period: filters.period } : undefined,
        }
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className={`${CARD} flex flex-wrap items-end gap-3 p-4`}>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          Month
          <input
            type="month"
            max={currentPeriod()}
            value={filters.period || ''}
            onChange={(e) => set({ period: e.target.value })}
            className={INPUT}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          Status
          <select
            value={filters.status || ''}
            onChange={(e) => set({ status: e.target.value })}
            className={INPUT}
          >
            <option value="">All</option>
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <div className="flex w-64 flex-col gap-1 text-xs text-gray-600">
          Consultant
          <ConsultantPicker
            value={consultant}
            onChange={(c) => {
              setConsultant(c);
              setOffset(0);
            }}
          />
        </div>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          Search
          <input
            type="search"
            placeholder="Invoice or eTIMS number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${INPUT} w-56`}
          />
        </label>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            className={`${SECONDARY} inline-flex items-center gap-1`}
          >
            <DownloadIcon fontSize="small" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            type="button"
            onClick={onCreate}
            className={`${PRIMARY} inline-flex items-center gap-1`}
          >
            <AddIcon fontSize="small" />
            Add for someone
          </button>
        </div>
      </div>
      {isLoading && (
        <div className={`${CARD} h-48 animate-pulse`} aria-label="Loading" />
      )}
      {error && !data && (
        <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
          {financeError(error, "We couldn't load the invoices.")}
        </div>
      )}
      {data && total === 0 && (
        <div className={`${CARD} p-10 text-center text-sm text-gray-600`}>
          No invoices match.
        </div>
      )}
      {data && total > 0 && (
        <section className={`${CARD} overflow-hidden`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <th className="py-2 pl-5 pr-2 font-medium">Consultant</th>
                <th className="px-2 py-2 font-medium">Month</th>
                <th className="px-2 py-2 font-medium">Invoice number</th>
                <th className="px-2 py-2 text-right font-medium">Amount</th>
                <th className="px-2 py-2 font-medium">Status</th>
                <th className="py-2 pl-2 pr-5 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((invoice) => (
                <tr
                  key={invoice.id}
                  onClick={() => onOpen(invoice.id)}
                  className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50"
                >
                  <td className="py-2.5 pl-5 pr-2">
                    <span className="text-gray-900">
                      {invoice.administrator_name ||
                        invoice.administrator_email}
                    </span>
                    {invoice.administrator_name && (
                      <span className="block text-xs text-gray-500">
                        {invoice.administrator_email}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 text-gray-700">
                    {periodLabel(invoice.period)}
                  </td>
                  <td className="px-2 py-2.5 text-gray-700">
                    {invoice.invoice_number}
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums text-gray-900">
                    {formatAmount(invoice.amount, invoice.currency)}
                  </td>
                  <td className="px-2 py-2.5">
                    <StatusBadge status={invoice.status} />
                  </td>
                  <td className="whitespace-nowrap py-2.5 pl-2 pr-5 text-xs text-gray-500">
                    {formatDateTime(invoice.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager
            offset={offset}
            count={rows.length}
            total={total}
            onPage={setOffset}
          />
        </section>
      )}
    </div>
  );
}

function Missing() {
  const [period, setPeriod] = useState(currentPeriod());
  const [confirmAll, setConfirmAll] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const { data, isLoading, error } = useMissing(period);
  const send = useSendInvoiceReminders();
  const people = data?.administrators ?? [];
  const expected = people.filter((p) => p.expected);

  const remind = (ids) => {
    setSendingId(ids ? ids[0] : 'all');
    send.mutate(
      { period, administratorIds: ids },
      {
        onSuccess: (result) => {
          setConfirmAll(false);
          const count = result?.count ?? 0;
          if (count) {
            toast.success(
              `Reminder sent to ${count} ${count === 1 ? 'person' : 'people'}.`
            );
          } else {
            toast('No reminder sent: already reminded today.');
          }
        },
        onError: (err) => {
          setConfirmAll(false);
          toast.error(financeError(err, "We couldn't send the reminder."));
        },
        onSettled: () => setSendingId(null),
      }
    );
  };

  return (
    <div className="space-y-4">
      <div className={`${CARD} flex flex-wrap items-end gap-3 p-4`}>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          Month
          <input
            type="month"
            max={currentPeriod()}
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              setConfirmAll(false);
            }}
            className={INPUT}
          />
        </label>
        {data && (
          <p className="pb-1.5 text-sm text-gray-700">
            {people.length}{' '}
            {people.length === 1 ? 'person hasn’t' : 'people haven’t'} submitted
            for {periodLabel(period)}; {expected.length} of them{' '}
            {expected.length === 1 ? 'is' : 'are'} expected to.
          </p>
        )}
        {expected.length > 0 && !confirmAll && (
          <button
            type="button"
            onClick={() => setConfirmAll(true)}
            className={`${PRIMARY} ml-auto`}
          >
            Remind all expected…
          </button>
        )}
        {confirmAll && (
          <span className="ml-auto flex items-center gap-2 text-sm text-gray-700">
            Email {expected.length}{' '}
            {expected.length === 1 ? 'person' : 'people'} about{' '}
            {periodLabel(period)}?
            <button
              type="button"
              onClick={() => remind(undefined)}
              disabled={send.isLoading}
              className={PRIMARY}
            >
              {sendingId === 'all' ? 'Sending…' : 'Send'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmAll(false)}
              className={SECONDARY}
            >
              Cancel
            </button>
          </span>
        )}
      </div>
      {isLoading && (
        <div className={`${CARD} h-32 animate-pulse`} aria-label="Loading" />
      )}
      {error && (
        <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
          {financeError(error, "We couldn't load who's missing.")}
        </div>
      )}
      {people.length > 0 && (
        <ul className={`${CARD} m-0 list-none divide-y divide-gray-100 p-0`}>
          {people.map((p) => (
            <li
              key={p.administrator_id}
              className="flex flex-wrap items-center justify-between gap-3 px-5 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm">
                  <span className="text-gray-900">{p.name || p.email}</span>
                  {p.name && (
                    <span className="ml-2 text-xs text-gray-500">
                      {p.email}
                    </span>
                  )}
                  {p.expected && (
                    <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                      Expected
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-gray-500">
                  {p.last_reminded_at
                    ? `Last reminded ${formatDateTime(p.last_reminded_at)}`
                    : 'Not reminded yet'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => remind([p.administrator_id])}
                disabled={send.isLoading}
                className={SECONDARY}
              >
                {sendingId === p.administrator_id
                  ? 'Sending…'
                  : 'Send reminder'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Audit() {
  const [offset, setOffset] = useState(0);
  const { data, isLoading, error } = useAudit({}, offset);
  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;
  if (isLoading)
    return (
      <div className={`${CARD} h-48 animate-pulse`} aria-label="Loading" />
    );
  if (error) {
    return (
      <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
        {financeError(error, "We couldn't load the audit trail.")}
      </div>
    );
  }
  return (
    <section className={`${CARD} overflow-hidden`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
            <th className="py-2 pl-5 pr-2 font-medium">When</th>
            <th className="px-2 py-2 font-medium">Who</th>
            <th className="px-2 py-2 font-medium">Action</th>
            <th className="py-2 pl-2 pr-5 font-medium">Details</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr
              key={e.id}
              className="border-b border-gray-100 last:border-0 align-top"
            >
              <td className="whitespace-nowrap py-2 pl-5 pr-2 text-xs text-gray-600">
                {formatDateTime(e.at)}
              </td>
              <td className="px-2 py-2 text-gray-800">
                {e.actor_email || 'system'}
              </td>
              <td className="px-2 py-2 text-gray-800">{e.action}</td>
              <td className="max-w-md break-words py-2 pl-2 pr-5 text-xs text-gray-500">
                {auditDetails(e.details)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager
        offset={offset}
        count={entries.length}
        total={total}
        onPage={setOffset}
      />
    </section>
  );
}

function Access() {
  const { data, isLoading, error } = useGrants();
  const grant = useGrantAccess();
  const revoke = useRevokeAccess();
  const [person, setPerson] = useState(null);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [revoking, setRevoking] = useState(null);
  const grants = data?.grants ?? [];
  const active = grants.filter((g) => !g.revoked_at);
  const past = grants.filter((g) => g.revoked_at);

  const add = () =>
    grant.mutate(
      {
        ...(person
          ? { administrator_id: person.administrator_id }
          : { email: email.trim() }),
        note: note.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Finance access given.');
          setPerson(null);
          setEmail('');
          setNote('');
        },
        onError: (err) =>
          toast.error(financeError(err, "We couldn't give access.")),
      }
    );
  const remove = (g) =>
    revoke.mutate(
      { administratorId: g.administrator_id, note: '' },
      {
        onSuccess: () => {
          toast.success('Finance access removed.');
          setRevoking(null);
        },
        onError: (err) => {
          setRevoking(null);
          toast.error(financeError(err, "We couldn't remove access."));
        },
      }
    );

  if (isLoading)
    return (
      <div className={`${CARD} h-48 animate-pulse`} aria-label="Loading" />
    );
  if (error) {
    return (
      <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
        {financeError(error, "We couldn't load access.")}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <section className={`${CARD} space-y-3 p-5`}>
        <div>
          <p className="text-sm font-semibold text-gray-900">
            Who can see everyone&apos;s invoices
          </p>
          <p className="text-xs text-gray-500">
            Only people on this list. Roles don&apos;t give access, not even
            Super Admin. Every change is recorded in the audit trail.
          </p>
        </div>
        <ul className="m-0 list-none divide-y divide-gray-100 p-0">
          {active.map((g) => (
            <li
              key={g.id}
              className="flex items-center justify-between gap-3 py-2"
            >
              <div>
                <p className="text-sm text-gray-900">
                  {g.name || g.email}{' '}
                  <span className="text-xs text-gray-500">
                    {g.name && g.email}
                  </span>
                </p>
                <p className="text-[11px] text-gray-500">
                  Given {formatDateTime(g.granted_at)} by {g.granted_by}
                  {g.note && ` · ${g.note}`}
                </p>
              </div>
              {revoking === g.administrator_id ? (
                <span className="flex items-center gap-2 text-xs">
                  Remove access?
                  <button
                    type="button"
                    onClick={() => remove(g)}
                    disabled={revoke.isLoading}
                    className={PRIMARY}
                  >
                    Remove
                  </button>
                  <button
                    type="button"
                    onClick={() => setRevoking(null)}
                    className="text-gray-500 hover:underline"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setRevoking(g.administrator_id)}
                  disabled={active.length <= 1}
                  title={
                    active.length <= 1
                      ? 'The last person with access can’t be removed.'
                      : undefined
                  }
                  className="text-xs text-claret-500 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
                >
                  Remove…
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section className={`${CARD} space-y-3 p-5`}>
        <p className="text-sm font-semibold text-gray-900">Give access</p>
        <ConsultantPicker value={person} onChange={setPerson} />
        {!person && (
          <input
            type="email"
            aria-label="Or their email"
            placeholder="…or type their email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${INPUT} w-full`}
          />
        )}
        <input
          type="text"
          aria-label="Reason"
          placeholder="Reason (recorded in the audit trail)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className={`${INPUT} w-full`}
        />
        <button
          type="button"
          onClick={add}
          disabled={(!person && !email.trim()) || grant.isLoading}
          className={PRIMARY}
        >
          {grant.isLoading ? 'Giving access…' : 'Give access'}
        </button>
      </section>
      {past.length > 0 && (
        <section className={`${CARD} space-y-2 p-5`}>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Removed
          </p>
          <ul className="m-0 list-none space-y-1 p-0">
            {past.map((g) => (
              <li key={g.id} className="text-xs text-gray-600">
                {g.name || g.email}: given {formatDateTime(g.granted_at)},
                removed {formatDateTime(g.revoked_at)}
                {g.revoked_by && ` by ${g.revoked_by}`}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// Everyone's invoices, for finance holders only. Access comes from the
// finance grant list (GET /finance/access), never from roles.
export default function FinanceInvoices() {
  const access = useFinanceAccess();
  const [params, setParams] = useSearchParams();
  const [modal, setModal] = useState(null); // {mode: 'view', id} | {mode: 'new'}
  const tab = TABS.some((t) => t.key === params.get('tab'))
    ? params.get('tab')
    : 'invoices';

  if (access.isLoading) {
    return (
      <MainContent full>
        <div className={`${CARD} h-48 animate-pulse`} aria-label="Loading" />
      </MainContent>
    );
  }
  if (access.data !== true) return <Navigate to="/" replace />;

  const close = () => setModal(null);
  return (
    <MainContent full>
      <div className="space-y-5 pb-10">
        <header>
          <h1 className="text-2xl font-semibold text-gray-900">
            Consultant invoices
          </h1>
          <p className="text-xs text-gray-500">
            Everyone&apos;s monthly invoices and KRA eTIMS receipts. Every view,
            download and change is recorded in the audit trail.
          </p>
        </header>
        <nav className="flex flex-wrap gap-1 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() =>
                setParams(t.key === 'invoices' ? {} : { tab: t.key }, {
                  replace: true,
                })
              }
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                tab === t.key
                  ? 'border-claret-500 text-claret-500'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
        {tab === 'invoices' && (
          <Submissions
            onOpen={(id) => setModal({ mode: 'view', id })}
            onCreate={() => setModal({ mode: 'new' })}
          />
        )}
        {tab === 'missing' && <Missing />}
        {tab === 'reminders' && <FinanceReminders />}
        {tab === 'audit' && <Audit />}
        {tab === 'access' && <Access />}
      </div>
      <Modal
        modalOpen={Boolean(modal)}
        onClose={close}
        title={modal?.mode === 'new' ? 'Add an invoice for someone' : 'Invoice'}
      >
        {modal?.mode === 'new' && (
          <InvoiceForm scope="all" onDone={close} onCancel={close} />
        )}
        {modal?.mode === 'view' && (
          <HolderInvoiceDetail id={modal.id} onClose={close} />
        )}
      </Modal>
    </MainContent>
  );
}
